/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import {PackageItem} from "types/entities/PackageItem";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {Package} from "types/entities/Package";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {BookingFilter, BookingProcess} from "types/app";
import {filterPackageItemsByType, subtractDaysFromDates, uniqueFilter} from "utils/utility";
import {PersonType} from "types/enums/PersonType";
import {PackageCategory} from "types/enums/PackageCategory";
import Dates from "utils/Dates";
import {PackagesCart} from "types/entities/PackagesCart";
import {VoucherDiscountType, VoucherItem} from "types/entities/VoucherItem";
import {HallCode} from "types/enums/HallCode";
import {GroupTypeId} from "types/enums/GroupTypeId";
import {
    DEFAULT_DURATION,
    DEFAULT_FAMILY_CREDIT_PACKAGE_AMOUNT,
    DEFAULT_HALL,
    DEFAULT_RESERVATION_DAYS_FROM_NOW,
    DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT,
    FERIENPASS_MINIMUM_DATE,
    IS_PROD,
    MAX_MONTH_COUNT_FOR_AVAILABLE_DATES,
    SHOW_ITEM_SLOT_RANGE_ONLY,
    SHOW_ONLY_RESERVATION_START,
} from "helpers/config";
import BookingAction, {PackageCompetition, PackageUpsell} from "store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {Hall} from "types/entities/Hall";
import {Timeslot} from "types/entities/Timeslot";
import {ActivityType} from "types/enums/ActivityType";
import {Activity} from "types/entities/Activity";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackagesCartItemSlot} from "types/entities/PackagesCartItemSlot";
import {ArticleTicketType} from "types/enums/ArticleTicketType";
import {CreditItem, PackagesItemByPerson, PackagesItemsPerson} from "types/entities/PackagesItems";
import {useTypedSelector} from "helpers/reducers";
import {addMonths} from "date-fns";
import {PackageCategoryEntity} from "types/entities/PackageCategoryEntity";
import {useVoucherTypeByArticle} from "utils/voucherUtils";
import {Reservation} from "types/entities/Reservation";
import {HallOpeningHourEntries} from "types/entities/HallOpeningHourEntries";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";
import {
    getInitialPackageItemId,
    getRecommendedOrHighestAvailablePackageItem,
    getRecommendedOrHighestCommonAvailableDuration,
    isSelectablePackageItem,
} from "utils/packageDurationUtils";
import {useTranslation} from "react-i18next";

/* Find PackageItems by provided PackageItemPersonType
 * @param packageItem[], PackageItemPersonType
 */
export const findPackageItemByPersonType = (packageItems: PackageItem[], personType: PackageItemPersonType): PackageItem | undefined => {
    return packageItems.find(packageItem => {
        if (typeof packageItem.person_type === "undefined" || packageItem.type === PackageItemItemType.Upsell) return null;
        if (packageItem.is_active === 1) {
            return packageItem.person_type === personType;
        }
        return null;
    });
};

export const findPackageItemByPersonTypeAndCount = (packageItems: PackageItem[], personType: PackageItemPersonType, count: number): PackageItem => {
    const packageItemsByType = packageItems.filter(packageItem => {
        if (typeof packageItem.person_type === "undefined") return null;
        if (packageItem.type !== PackageItemItemType.PackageItem) return null;
        return packageItem.person_type === personType;
    });
    const packageItemByCount = packageItemsByType.find(packageItem => {
        if (packageItem.minimum_persons_count > count) return false;
        return packageItem.maximum_persons_count >= count;
    });
    return <PackageItem>packageItemByCount;
};

/* Return only the items of type PackageItemItemType.PackageItem  */
export const getBookingPackageItems = (Package: Package): PackageItem[] => {
    return Package.packageItems.filter(packageItem => packageItem.type === PackageItemItemType.PackageItem);
};

/*Compare bookingFilter to selected package requirements */
export const filterFitsWithPackageRequirements = (Package: Package | null, bookingFilter: BookingFilter): boolean => {
    let disabled = true;
    let packages: PackageItem[] | false;
    if (!Package) {
        return true;
    }
    if (!getBookingPackageItems(Package)) {
        return true;
    } else {
        packages = getBookingPackageItems(Package);
    }

    let hasEnoughAdults = false;
    let hasTooMuchAdults = false;

    let hasEnoughChildren = false;
    let hasTooMuchChildren = false;

    let hasEnoughPersonsCount = false;
    let hasTooMuchPersonsCount = false;
    let isBookableByHour = false;

    const adultPackageItem = findPackageItemByPersonTypeAndCount(packages, PackageItemPersonType.Adult, bookingFilter.adults);
    const childPackageItem = findPackageItemByPersonTypeAndCount(packages, PackageItemPersonType.Child, bookingFilter.children);
    if (adultPackageItem) {
        //first check if there are enough adults, independent from Package type
        hasEnoughAdults = adultPackageItem.minimum_persons_count <= +bookingFilter.adults;
        hasTooMuchAdults = adultPackageItem.maximum_persons_count < +bookingFilter.adults;
    }

    if (childPackageItem) {
        /*first check if there are enough children, independent from Package type*/
        hasEnoughChildren = childPackageItem.minimum_persons_count <= +bookingFilter.children;
        hasTooMuchChildren = childPackageItem.maximum_persons_count < +bookingFilter.children;
    }

    if (adultPackageItem && childPackageItem) {
        /* both packageItems exists, bookingFilters should be counted together */
        if (Package?.min_drivers_count <= +bookingFilter.adults + bookingFilter.children) disabled = false;
    }

    hasEnoughPersonsCount = Package?.min_drivers_count <= +bookingFilter.adults + bookingFilter.children;
    hasTooMuchPersonsCount = Package?.max_drivers_count < +bookingFilter.adults + bookingFilter.children;
    isBookableByHour = !!Package?.extra.bookableByHour;

    const minDaysFromNow = Package.extra.minDaysFromNow;
    if (minDaysFromNow && minDaysFromNow > 0) {
        const date1 = subtractDaysFromDates(new Date(bookingFilter.date), minDaysFromNow);
        const date2 = new Date();
        date2.setHours(0);
        date2.setMinutes(0);
        date2.setSeconds(0);
        disabled = date1 < date2;
    }

    /*if packageItems has only one of personType, we take only that kind in consideration */
    if (adultPackageItem && hasEnoughAdults && childPackageItem && hasEnoughChildren) {
        disabled = false;
    }

    if (adultPackageItem && childPackageItem && hasEnoughPersonsCount && Package?.min_drivers_count > 0) {
        disabled = false;
    }

    if (hasTooMuchAdults || hasTooMuchChildren) {
        disabled = true;
    }

    disabled = !hasEnoughPersonsCount;

    if (hasTooMuchPersonsCount) {
        disabled = true;
    }

    if (isBookableByHour && bookingFilter.hours_count == 0) {
        disabled = true;
    }
    // console.log(hasEnoughChildren);
    // console.log(hasTooMuchAdults);
    // console.log(hasTooMuchChildren);
    // console.log("------------");
    // console.log(hasEnoughPersonsCount);
    // console.log(hasTooMuchPersonsCount);
    //
    // console.log(adultPackageItem);
    // console.log(childPackageItem);
    // console.log("disabled", disabled);
    return disabled;
};

/* filter packages based on minimum and maximum drivers count compared to bookingFilter */
export const packagesFilter = (packages: Package[], bookingFilter: BookingFilter) => {
    const {adults, children, groupType, category} = bookingFilter;
    const filteredPackages = Object.values(packages);
    if (filteredPackages.length === 0) return [];

    const filteredCounts = filteredPackages.filter(packageRow => {
        if (adults + children === 0) {
            return true;
        }
        return packageRow.min_drivers_count <= adults + children && packageRow.max_drivers_count >= adults + children;
    });

    const filteredByCategory = filteredCounts.filter(packageRow => {
        if ((category && category === PackageCategory.All) || !category) {
            return true;
        }
        if (category && category === PackageCategory.Event && packageRow.category === category) {
            return true;
        }
        if (category && category === PackageCategory.Action && packageRow.category === category) {
            return true;
        }
        if (
            category &&
            category === PackageCategory.Others &&
            packageRow.category !== PackageCategory.Event &&
            packageRow.category !== PackageCategory.Action
        ) {
            return true;
        }
        return false;
    });

    return filteredByCategory.filter(packageRow => {
        if (packageRow.category === PackageCategory.Profile) {
            return false;
        }
        if (groupType.length === 0) {
            return true;
        }
        if (groupType.length > 0 && packageRow.extra.groupType && packageRow.extra.groupType.length == 0) {
            return false;
        }
        if (packageRow.extra.groupType) {
            if (packageRow.extra.groupType.length == 0) {
                return true;
            }
            if ("groupType" in packageRow.extra === false) {
                return true;
            }
        }
        if (packageRow.extra.groupType) {
            if (typeof groupType == "object") {
                return (
                    groupType.filter(gT => {
                        if (packageRow.extra.groupType) {
                            return packageRow.extra.groupType.includes(+gT);
                        }
                        return false;
                    }).length > 0
                );
            }
            if (typeof groupType == "string") {
                return packageRow.extra.groupType.filter(gt => gt === +groupType).length > 0;
            }
        }
    });
};

/* filter unique activities from package */
export const filteredActivities = (packageDetail): number[] => {
    return uniqueFilter(packageDetail.packageItems.map(packageItem => packageItem.activity_id)); //skip null activities
};

/* filter unique packageItems based on activity */
export const uniquePackageWithActivityItem = (packageDetail, withActivity?: boolean, personType: PersonType = PersonType.Adult): PackageItem[] => {
    const filteredActivitiesArray = filteredActivities(packageDetail).filter(f => (withActivity ? f : f === null));
    const filteredPackages = filteredActivitiesArray.map(activityNumber => {
        const filteredPackageItems = filterPackageItemsByType(packageDetail, personType);
        const filteredPackageItemIndex = filteredPackageItems.findIndex(packageItem => packageItem.activity_id === activityNumber);
        return filteredPackageItems[filteredPackageItemIndex];
    });
    return filteredPackages.filter(packageItem => packageItem); //ignore undefined and null
};

/***
 * Return upsell with extra_heat field
 * @param packageDetail
 * @param personType
 */
export const getExtraHeatPackageItem = (packageDetail: Package, personType: PackageItemPersonType = PackageItemPersonType.Adult): PackageItem | undefined => {
    return packageDetail.packageItems
        .filter(packageItem => packageItem.person_type === personType)
        .find(packageItem => {
            if (packageItem.type === PackageItemItemType.Upsell) {
                return packageItem.extra.extra_heat;
            }
            return false;
        });
};

export const minimumPackagePrice = (packageDetail: Package, personType: PackageItemPersonType = PackageItemPersonType.Adult): PackageItem => {
    const filteredPackageByPersonType = packageDetail.packageItems
        .filter(packageItem => packageItem.person_type === personType)
        .filter(packageItem => packageItem.type === PackageItemItemType.PackageItem);
    const minimum = Math.min(...filteredPackageByPersonType.map(packageItem => packageItem.price));
    const resolvedPackageItem: PackageItem = <PackageItem>filteredPackageByPersonType.find(item => item.price == minimum);
    return resolvedPackageItem;
};

export const isWeekdayDiscountedPackageItem = (weekDay: number, packageItem: PackageItem): boolean => {
    if (!packageItem.extra.includeWeekDays) {
        return false;
    }
    const days = packageItem.extra.includeWeekDays.filter(includeWeekDay => includeWeekDay === weekDay);
    return days.length > 0;
};

export const packageHasUpsellItems = (Package: Package): boolean => {
    return Package.packageItems.filter((packageItem: PackageItem): boolean => packageItem.type === PackageItemItemType.Upsell).length > 0;
};

export const packageMinimalDate = (Package: Package): Date => {
    const today = new Date();
    if (Package.extra.releaseDate) {
        const releaseDate = new Date(Package.extra.releaseDate);
        if (today.getTime() > releaseDate.getTime()) {
            return today;
        }
        return new Date(Package.extra.releaseDate);
    }
    if (Package.extra.minDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), Package.extra.minDaysFromNow).toString());
    }
    return today;
};

export const packageCategoryMinimalDate = (packageCategoryEntity: PackageCategoryEntity): Date => {
    const today = new Date();

    if (packageCategoryEntity.extra?.releaseDate) {
        const releaseDate = new Date(packageCategoryEntity.extra.releaseDate);

        if (today.getTime() > releaseDate.getTime()) {
            return today;
        }
        return releaseDate;
    }

    if (packageCategoryEntity.id == PackageCategory.FerienPassB || packageCategoryEntity.id == PackageCategory.FerienPass) {
        return FERIENPASS_MINIMUM_DATE;
    }

    if (packageCategoryEntity.extra?.minDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), packageCategoryEntity.extra.minDaysFromNow).toString());
    }

    return today;
};

export const packageMaximumDate = (Package: Package): Date => {
    const today = new Date();

    if (Package.extra.onlyOnDate) {
        return new Date(Package.extra.onlyOnDate);
    }

    if (Package.extra.maxDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), Package.extra.maxDaysFromNow).toString());
    }
    return today;
};

export const packageCategoryMaximumDate = (packageCategoryEntity: PackageCategoryEntity): Date => {
    const today = new Date();

    if (!packageCategoryEntity.extra) {
        return today;
    }
    if (packageCategoryEntity.extra.onlyOnDate) {
        return new Date(packageCategoryEntity.extra.onlyOnDate);
    }

    if (packageCategoryEntity.extra.maxDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), packageCategoryEntity.extra.maxDaysFromNow).toString());
    }
    //fallback
    return today;
};

/**
 * Stable function that returns weekday numbers from filterByWeekDay
 *
 * @param Package
 */
const getWeekDaysFromPackageExtra = (Package: Package) => {
    const weekDays: number[] = Package.extra.filterByWeekDay
        ? Package.extra.filterByWeekDay.map(packageExtraFilter => {
              const weekDay = packageExtraFilter.weekDay;
              if (weekDay === 7) {
                  return 0;
              }
              return weekDay;
          })
        : [];

    return weekDays;
};

/**
 * Stable function that returns weekday numbers from PackageCategory - hard predefined
 *
 * @param {PackageCategory} packageCategory
 */
const getWeekDaysFromPackageCategoryExtra = (packageCategory: PackageCategory): number[] => {
    const weekDays: (packageCategory: PackageCategory) => number[] = (packageCategory: PackageCategory) => {
        switch (packageCategory) {
            case PackageCategory.Action:
                return [0, 1, 2, 3, 4];
            case PackageCategory.FerienPass:
                return [1, 2, 3, 4, 5];
            case PackageCategory.FerienPassB:
                return [1, 2, 3, 4, 5];
            case PackageCategory.MegaFunSchool:
                return [1, 2, 3, 4, 5];
            case PackageCategory.SchoolTrophy:
                return [1, 2, 3, 4, 5];
            default:
                return [];
        }
    };

    return weekDays(packageCategory);
};

/**
 * Stable function creates arrays of days that are included
 * @param date
 * @param month
 * @param weekDays
 */
const createMonthDateArray = (date, month, weekDays) => {
    const days: Date[] = [];
    while (date.getMonth() === month) {
        //repeat for the whole month from 1st date until end of month
        if (weekDays.includes(date.getDay())) {
            days.push(new Date(date));
        }
        date.setDate(date.getDate() + 1);
    }
    return days;
};

const generateConsecutiveMonths = (dateEntry: Date, monthIndex: number, weekDays: number[]) => {
    // Validate the input parameters
    if (!(dateEntry instanceof Date)) {
        throw new Error("dateEntry should be a valid Date object.");
    }
    if (typeof monthIndex !== "number" || monthIndex < 0 || monthIndex > 11) {
        throw new Error("monthIndex should be an integer between 0 and 11.");
    }

    // Create an array to store the first days of consecutive months
    const firstDays: Date[] = [];

    // Clone the dateEntry to avoid modifying the original object
    const currentDate = new Date(dateEntry);

    // Set the month of the currentDate to the specified monthIndex
    currentDate.setMonth(monthIndex);

    // Use a while loop to generate first days of 12 consecutive months
    let monthCount = 0;
    while (monthCount < 12) {
        // Push the first day of the current month to the array
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        firstDays.push(...createMonthDateArray(newDate, newDate.getMonth(), weekDays));

        // Move to the next month
        currentDate.setMonth(currentDate.getMonth() + 1);

        monthCount++;
    }
    return firstDays;
};

export const packageExcludeDates = (Package: Package, month: number, type: "exclude" | "include" = "exclude"): Date[] => {
    const weekDays = getWeekDaysFromPackageExtra(Package);
    let days: Date[] = [];
    const year = new Date().getFullYear();
    const date = new Date(year, month, 1);
    const allValid = [0, 1, 2, 3, 4, 5, 6];
    const filterWeekDays = allValid.filter(num => {
        if (weekDays.length == 0) {
            return type === "include";
        }
        return type === "include" ? weekDays.includes(num) : !weekDays.includes(num);
    });

    if (filterWeekDays.length > 0) {
        days = generateConsecutiveMonths(date, month, filterWeekDays);
    }

    return days;
};

export const useHallHolidays = (selectedPackageCategory: PackageCategory | null | undefined) => {
    const hallState = useTypedSelector(state => state.hallState.hall);

    const getHallHolidays = hall_id => {
        if (!hallState?.futureHallHolidays) {
            return [];
        }

        return hallState.futureHallHolidays
            .filter(hallHoliday => hallHoliday.hall_id === hall_id)
            .map((hallHoliday, index) => {
                return {
                    date: hallHoliday.date,
                    type: hallHoliday.type,
                    dayOfWeek: new Date(hallHoliday.date).getDay(),
                };
            });
    };

    const getHallOpeningHoursEntries = hall_id => {
        if (!hallState?.hallOpeningHoursEntries) {
            return [];
        }
        return hallState.hallOpeningHoursEntries
            .filter((hallOpeningHoursEntry: HallOpeningHourEntries) => hallOpeningHoursEntry.hall_id === hall_id)
            .map(hallOpeningHoursEntry => {
                const isOpen = hallOpeningHoursEntry.from_hours !== hallOpeningHoursEntry.to_hours;
                return {
                    date: hallOpeningHoursEntry.date,
                    type: isOpen ? "open" : "closed",
                    family_action: hallOpeningHoursEntry.family_ticket,
                    dayOfWeek: new Date(hallOpeningHoursEntry.date).getDay(),
                };
            });
    };

    const includedDays = getHallOpeningHoursEntries(1).filter(day => {
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.FerienPass) {
            return day.type === "open" && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay());
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.MegaFunSchool) {
            return day.type === "open" && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay());
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.SchoolTrophy) {
            return day.type === "open" && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay());
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.FerienPassB) {
            if (new Date(day.date).getMonth() == 7 && new Date(day.date).getDate() == 1) {
                return false;
            }
            return day.type === "open" && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay());
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.Action) {
            return day.type === "open" && day.family_action;
        }
        return day.type === "open";
    });
    // console.log("includedDays", includedDays);
    const formatDate = date => {
        const d = new Date(date);
        return d.getFullYear() + "-" + (d.getMonth() + 1).toString().padStart(2, "0") + "-" + d.getDate().toString().padStart(2, "0");
    };

    const includedDates = includedDays.map(day => {
        const datum = new Date(formatDate(day.date));
        datum.setHours(0);
        return datum;
    });

    const filteredExcludedDays = excludeDays =>
        excludeDays.filter(excludedDay => {
            return !includedDays.find(includedDay => {
                return formatDate(excludedDay) === formatDate(includedDay.date);
            });
        });

    return {
        formatDate,
        includedDays,
        includedDates,
        filteredExcludedDays,
        getHallHolidays,
    };
};

export const packageCategoryExcludeDates = (packageCategory: PackageCategory, month: number, type: "exclude" | "include" = "exclude"): Date[] => {
    const weekDays = getWeekDaysFromPackageCategoryExtra(packageCategory);
    let days: Date[] = [];
    const year = new Date().getFullYear();
    const date = new Date(year, month, 1);
    const allValid = [0, 1, 2, 3, 4, 5, 6];
    const filterWeekDays = allValid.filter(num => {
        if (weekDays.length == 0) {
            return type === "include";
        }
        return type === "include" ? weekDays.includes(num) : !weekDays.includes(num);
    });

    if (filterWeekDays.length > 0) {
        days = generateConsecutiveMonths(date, month, filterWeekDays);
    }

    return days;
};

export const getPackageRespectsTimeslotSize = (Package: Package): boolean => {
    const category = Package.category;
    switch (category) {
        case PackageCategory.Event:
            return false;
        case PackageCategory.Main:
            return false;
        case PackageCategory.General:
            return true;
        case PackageCategory.Profile:
            return true;
        case PackageCategory.Season:
            return true;
    }

    return true;
};

export const getPackageTimeslotsCount = (Package: Package, personType: PackageItemPersonType = PackageItemPersonType.Adult): number => {
    const packageCount = Package.packageItems
        .filter((packageItem: PackageItem) => packageItem.person_type === personType)
        .reduce((prev: number, cur: PackageItem) => {
            const packageItemTimeslotsCount = cur.article.race_count_total;
            if (!prev) {
                return packageItemTimeslotsCount;
            }
            return prev + packageItemTimeslotsCount;
        }, 0);
    return packageCount;
};

export const isYearly = (Package: Package) => Package.category === PackageCategory.Season;
export const isThreeTwo = (Package: Package) => Package.category === PackageCategory.ThreeTwo;
export const isGiftCard = (Package: Package) => Package.category === PackageCategory.Giftcard;
export const isEvent = (Package: Package) => Package.category === PackageCategory.Event;
export const isAction = (Package: Package) => Package.category === PackageCategory.Action;
export const isGroupTypeChildren = (Package: Package) => Package.extra.groupType && Package.extra.groupType.includes(GroupTypeId.Children);

export const packagesCartContainsUpsells = (packagesCart: PackagesCart, upsellOptionType?: boolean): boolean => {
    if (
        typeof packagesCart.packagesCartItems?.filter(packagesCartItem => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell).length === "number"
    ) {
        if (typeof upsellOptionType !== "undefined") {
            return (
                packagesCart.packagesCartItems
                    ?.filter(packagesCartItem => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell)
                    .filter(packagesCartItem => {
                        if (typeof packagesCartItem !== "undefined" && typeof packagesCartItem.packageItem !== "undefined") {
                            // @ts-ignore
                            return packagesCartItem?.packageItem?.extra.upsell_option >= 0;
                        }
                    }).length > 0
            );
        }

        return packagesCart.packagesCartItems?.filter(packagesCartItem => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell).length > 0;
    }
    return false;
};

export const getPersonsTypeFraction = (totalPersons, limit) => (totalPersons <= limit ? totalPersons / limit : 1);

/**
 *
 * @param packageRow
 * @param eligibleVoucher
 * @return number
 */
export const voucherDiscount = (packageRow: Package, eligibleVoucher: VoucherItem): number => {
    const {getVoucherType} = useVoucherTypeByArticle();
    let discountedPrice: number = packageRow.price;
    const packageArticle = packageRow.packageItems.map(packageItem => packageItem.article);
    const voucherDiscountType = getVoucherType(eligibleVoucher).type;

    if (!IS_PROD) {
        // console.log(voucherDiscountType);
    }

    switch (voucherDiscountType) {
        case VoucherDiscountType.Percent:
            return (discountedPrice = packageRow.price * ((100 - getVoucherType(eligibleVoucher).value) / 100));
        case VoucherDiscountType.Race:
            const voucherArticleMatchRace = packageArticle.find(article => {
                return article.race_count_total == eligibleVoucher.article.race_count_total;
            });
            if (!IS_PROD) {
                console.log(voucherArticleMatchRace);
            }
            return (discountedPrice = voucherArticleMatchRace ? packageRow.price - voucherArticleMatchRace.article_value : packageRow.price);
        case VoucherDiscountType.Activity:
            const voucherArticleMatchActivity = packageArticle.find(article => {
                // @ts-ignore // already matched with if(eligibleVoucher)
                return article.article_id === eligibleVoucher.article.article_id;
            });
            return (discountedPrice = voucherArticleMatchActivity ? packageRow.price - voucherArticleMatchActivity.article_value : packageRow.price);
        default:
            return discountedPrice;
    }
};

export const isExclusiveAtSpreitenbach = (Package: Package, hall: Hall) => hall.hall_code === HallCode.Spreitenbach && Package.rewrite_url === "exclusive";

export const getModuloMinutesByPackageAndHall = (Package: Package, hall: Hall) => {
    if (isGroupTypeChildren(Package)) {
        return 60;
    }

    if (isExclusiveAtSpreitenbach(Package, hall)) {
        return 30;
    }

    return hall.default_bookable_intervals ? hall.default_bookable_intervals : 60;
};

const getUpsellsForPackage = (Package: Package) => {
    return Package.packageItems
        .filter(packageItem => {
            if (packageItem.type === PackageItemItemType.Upsell) {
                return true;
            }
            return false;
        })
        .map(packageItem => packageItem.id);
};

export const useClearUpsells = (bookingProcess: BookingProcess) => {
    const dispatch = useDispatch();
    const selectedUpsells = bookingProcess.upsell.map(packageUpsell => packageUpsell.packageItem.id);

    const clearInvalidUpsells = (): any[] => {
        if (!bookingProcess.package) {
            return [];
        }

        const upsellsToNotClear = getUpsellsForPackage(bookingProcess.package).filter(upsellItem => selectedUpsells.includes(upsellItem));
        const upsellsCleared = bookingProcess.upsell.filter(packageUpsell => !upsellsToNotClear.includes(packageUpsell.packageItem.id));
        return upsellsCleared.map(upsell => {
            return dispatch(BookingAction.removeUpsell(upsell));
        });
    };

    return {
        clearInvalidUpsells,
    };
};

export const normalizeDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const getSelectedUpsellPackages = (selectedUpsells: PackageUpsell[]) =>
    selectedUpsells.map(upsell => {
        return {package_item_id: upsell.packageItem.id, quantity: +upsell.count, upsell_option: upsell.upsell_option};
    });

export const getSelectedCompetitionPackages = (selectedCompetitions: PackageCompetition[]) =>
    selectedCompetitions.map(competition => {
        return {package_item_id: competition.packageItem.id, quantity: +competition.count, upsell_option: competition.upsell_option};
    });
//
export const getAvailableDatesForCategory = (
    selectedCategory: PackageCategory,
    packageEntity: PackageCategoryEntity,
    excludedDates: Date[],
    includedDays: Date[]
) => {
    const maxDate = addMonths(new Date(), MAX_MONTH_COUNT_FOR_AVAILABLE_DATES);
    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0);
    const minimalDatePreset = new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);
    const minimalDate = selectedCategory === null ? new Date(minimalDatePreset) : packageCategoryMinimalDate(packageEntity);

    minimalDate.setHours(0, 0, 0, 0);
    const allDatesFromThisMonth = packageCategoryExcludeDates(selectedCategory, minimalDate.getMonth(), "include");
    allDatesFromThisMonth.push(...includedDays);

    const uniqueDates = allDatesFromThisMonth.reduce((acc: Date[], date) => {
        const duplicate = acc.find(a => date.getTime() === a.getTime());
        if (!duplicate) {
            acc.push(date);
        }
        return acc;
    }, []);

    const sorted = uniqueDates.sort((a: Date, b: Date) => (a.getTime() >= b.getTime() ? 1 : -1));
    return sorted.filter((date: Date) => {
        const exclDateExists = excludedDates.some(exclDate => date.toDateString() === exclDate.toDateString());

        if (exclDateExists || date.getTime() < minimalDate.getTime()) {
            return false;
        }

        if (date && maxDate && normalizeDate(maxDate) < normalizeDate(date)) {
            return false;
        }

        return date.getTime() >= lastMidnight.getTime();
    });
};

export const getAvailableDates = (selectedPackage: Package, excludedDates: Date[]) => {
    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0); // Midnight today

    const minimalDate = packageMinimalDate(selectedPackage);
    minimalDate.setHours(0, 0, 0, 0); // Start of the minimal date

    const allDatesFromThisMonth = packageExcludeDates(selectedPackage, minimalDate.getMonth(), "include");
    const maximumDate = packageMaximumDate(selectedPackage);
    maximumDate.setHours(23, 59, 59, 999); // End of the maximum date

    // Set excluded dates to midnight for accurate comparison
    const excludedDateSet = new Set(
        excludedDates.map(exclDate => {
            const date = new Date(exclDate);
            date.setHours(0, 0, 0, 0); // Set to midnight
            return date.toISOString().split("T")[0]; // Compare as YYYY-MM-DD
        })
    );

    return allDatesFromThisMonth.filter(date => {
        const dateStr = date.toISOString().split("T")[0]; // Convert to YYYY-MM-DD for comparison
        if (excludedDateSet.has(dateStr)) {
            return false; // Excluded date
        }

        const time = date.getTime();
        return time >= minimalDate.getTime() && time <= maximumDate.getTime() && time >= lastMidnight.getTime();
    });
};

export const getCurrentDateIndex = (availableDates, filter) => {
    return availableDates.findIndex(date => {
        const filterDateMidnight = new Date(filter.date);
        filterDateMidnight.setHours(0, 0, 0, 0);
        return date.getTime() === filterDateMidnight.getTime();
    });
};

export const fakeTimeslotMinute = selectedPackage => selectedPackage?.category === PackageCategory.Event && DEFAULT_HALL !== HallCode.ArboPark;

export const getTimeslotsAvailable = (flattenedTimeslots: Timeslot[], hallState: Hall | null, selectedPackage: Package | null) => {
    return flattenedTimeslots.filter((timeslot: Timeslot) => {
        if (!hallState) {
            return false;
        }
        const moduloMinutesByPackage = selectedPackage ? getModuloMinutesByPackageAndHall(selectedPackage, hallState) : 60;
        const moduloMinute = Dates.minute(timeslot.start_at) % moduloMinutesByPackage < timeslot.duration / 60;
        return !(fakeTimeslotMinute(selectedPackage) && !moduloMinute);
    });
};

export const getPossibleSlotIndex = (timeslot: Timeslot, type: "first" | "last", timeslotsAvailable: Timeslot[], selectedPackage: Package | null) => {
    const selectedSlotIndex = timeslotsAvailable.findIndex(timeslotAvailable => timeslotAvailable.id === timeslot.id);

    if (selectedSlotIndex === -1) {
        return null;
    }

    const selectedSlotTime = new Date(timeslotsAvailable[selectedSlotIndex].start_at);
    let hoursToAdd: number;

    if (type === "last") {
        hoursToAdd = selectedPackage?.extra.maxHoursForTimeslotSpan || 24;
    } else if (type === "first") {
        hoursToAdd = selectedPackage?.extra.maxHoursForTimeslotSpan ? +1 - selectedPackage?.extra.maxHoursForTimeslotSpan : 24;
    } else {
        throw new Error('Invalid type parameter. Use "first" or "last".');
    }

    selectedSlotTime.setHours(selectedSlotTime.getHours() + hoursToAdd);

    const comparisonFunction =
        type === "last"
            ? (timeslot: Timeslot) => new Date(timeslot.start_at).getTime() <= selectedSlotTime.getTime()
            : (timeslot: Timeslot) => new Date(timeslot.start_at).getTime() >= selectedSlotTime.getTime();

    const filteredTimeslots = timeslotsAvailable.filter(comparisonFunction);
    const possibleSlotIndex = type === "last" ? filteredTimeslots.length - 1 : 0;
    return possibleSlotIndex >= 0 ? timeslotsAvailable.indexOf(filteredTimeslots[possibleSlotIndex]) : null;
};

export const isKartingPackageItem = (packageItem: PackageItem, activities: Activity[]) => {
    const foundActivity = activities.find(activity => activity.id === packageItem.activity_id);
    return foundActivity?.type === ActivityType.Karting;
};

export const packageItemForPersonType = (personType: PackageItemPersonType, packageItems: PackageItem[]): PackageItem => {
    const item = findPackageItemByPersonType(packageItems, personType);
    return item as PackageItem;
};

export const packagesCartItemsOutstanding = (packagesCartItems: PackagesCartItem[]) => {
    return packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
        if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
            return false;
        }
        if (packagesCartItem.packageItem?.activity_id === null) {
            return true;
        }
    });
};

export const filterAccessItemSlots = (items: PackagesCartItemSlot[]) => {
    const filteredType = (packageItem: PackageItem): boolean => {
        if (packageItem.article.ticket_type === ArticleTicketType.Access) {
            return SHOW_ITEM_SLOT_RANGE_ONLY;
        }
        return false;
    };
    return items.filter((item, index) => {
        if (SHOW_ITEM_SLOT_RANGE_ONLY) {
            //show only first
            if (SHOW_ONLY_RESERVATION_START) {
                if (index === 0) {
                    return filteredType(item.packageItem);
                }
                return false;
            }
            if (index === 0 || index === items.length - 1) {
                return filteredType(item.packageItem);
            }
            return false;
        } else {
            return true;
        }
    });
};

export const getPackageCategoryName = (categorySlug: string) => {
    switch (categorySlug) {
        case PackageCategory.EventM:
            return "Mega";
        case PackageCategory.EventS:
            return "Fun";
        case PackageCategory.EventL:
            return "Ultra";
        case PackageCategory.Giftcard:
            return "Gift";
        case PackageCategory.Others:
        case PackageCategory.General:
            return "Standard";
        case PackageCategory.Action:
            return "Family";
        case PackageCategory.School:
            return "School";
        case PackageCategory.FerienPass:
            return "FerienPass";
        case PackageCategory.FerienPassB:
            return "FerienPass No ADults";
        case PackageCategory.Kinder:
            return "Kinder";
        default:
            return "---";
    }
};

export const usePackageItemKartingOptions = () => {
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const dispatch = useDispatch();
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const packagesCart: PackagesCart | null = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const availablePackagesCreditsItems: CreditItem[] | [] = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);
    const peopleCount: number = filter.adults + filter.children;

    /**
     * Retrieves a saved redux state package item based on the person index.
     * If the saved item is found, updates the selected package item,
     * withKarting value, and chosen duration based on the saved item.
     *
     * @param {number} personIndex - The index of the person associated with the saved package item
     * @param personType
     */
    const getSavedPackagesItemByPerson = (personIndex: number | null, personType: PackageItemPersonType) => {
        const savedItemIndex = selectedPackageItems.findIndex(item => item.person_type == personType && item.personIndex == personIndex);

        if (savedItemIndex !== -1 && availablePackageItems) {
            const savedItem = selectedPackageItems[savedItemIndex];
            const foundItemIndex = availablePackageItems.packageItems[savedItem.person_type]
                ? availablePackageItems.packageItems[savedItem.person_type].findIndex((item: PackagesItemsPerson) => item.id == savedItem.id)
                : -1;
            if (foundItemIndex !== -1 && personIndex == savedItem.personIndex) {
                return availablePackageItems.packageItems[savedItem.person_type][foundItemIndex];
            }
        } else {
            console.log("setting to default, should never happen because ", personIndex, personType);
            // Default values if no saved package item is found (likely for children or new entries)
            return null;
        }
    };

    /**
     * Calculates the least possible duration based on the provided input.
     *
     * @function
     * @name getLeastPossibleDuration
     * @param {number} [duration=240] - The initial duration to evaluate. Defaults to 240 if not provided.
     * @param withKarting
     * @returns {boolean} - Returns `true` if no suitable package item combination is found; otherwise, `false`.
     */
    const getLeastPossibleDuration = (duration: number = 240, withKarting?: boolean): boolean => {
        const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(duration, !!withKarting, PackageItemPersonType.Adult);
        console.log("getLeastPossibleDuration - packagesItemsPerson", duration, withKarting, packagesItemsPerson);
        return packagesItemsPerson === undefined;
    };

    /**
     * Sets default selected package items based on the number of people and selected package items.
     * Each person in the specified count gets a default package item based on their person type.
     * If a specific package item is selected for a person, that item is returned; otherwise, a default item is generated.
     */
    const setDefaultPackagesItems = (useCommonDuration: boolean = false) => {
        const availableItemsByPersonType = [
            ...(filter.adults > 0 ? [availablePackageItems?.packageItems[PackageItemPersonType.Adult] || []] : []),
            ...(filter.children > 0 ? [availablePackageItems?.packageItems[PackageItemPersonType.Child] || []] : []),
        ];
        const commonDuration = useCommonDuration
            ? getRecommendedOrHighestCommonAvailableDuration(availableItemsByPersonType, DEFAULT_DURATION, selectedTimeslot)
            : undefined;

        // A global row must stay unresolved when the represented person types
        // have no common package duration; never manufacture null mappings.
        if (useCommonDuration && commonDuration === undefined) {
            return;
        }

        const def: PackagesItemByPerson[] = Array.from({length: peopleCount}, (_, index) => {
            const personType: PackageItemPersonType = index < filter.adults ? PackageItemPersonType.Adult : PackageItemPersonType.Child;
            const personIndex = personType === PackageItemPersonType.Child ? 100 + (index - filter.adults) : index;
            const selectedPackageItem = selectedPackageItems.find(item => item.personIndex === personIndex && item.person_type === personType);
            const availableItems = availablePackageItems?.packageItems[personType] || [];
            const defaultPackageItem = useCommonDuration
                ? availableItems.find(item => item.duration === commonDuration && isSelectablePackageItem(item))
                : getRecommendedOrHighestAvailablePackageItem(availableItems, DEFAULT_DURATION, selectedTimeslot);

            return {
                personIndex,
                id: getInitialPackageItemId(selectedPackageItem, defaultPackageItem),
                person_type: personType,
                quantity: 1,
            };
        });

        dispatch(BookingAction.setSelectedPackageItems(def));
    };

    const setPackageItemHandler = (personIndex: number, id: number | null, person_type: PackageItemPersonType, quantity: number) => {
        dispatch(
            BookingAction.setSelectedPackageItem({
                id: id,
                personIndex: personIndex,
                person_type: person_type,
                quantity: quantity,
            })
        );
    };

    const setPackageCreditItemHandler = (personIndex: number, id: number | null, person_type: PackageItemPersonType, quantity: number) => {
        if (id === null) {
            dispatch(BookingAction.removeSelectedPackageCreditItem(personIndex));
            return;
        }
        dispatch(
            BookingAction.setSelectedPackageCreditItem({
                id: id,
                personIndex: personIndex,
                person_type: person_type,
                quantity: quantity,
            })
        );
    };

    const setPackageCreditItemsHandler = (nextPackagesCreditItems: PackagesItemByPerson[]) => {
        if (nextPackagesCreditItems.length === 0) {
            dispatch(BookingAction.resetSelectedPackageCreditItems());
            console.log("got here now");
            return;
        }
        dispatch(BookingAction.setSelectedPackageCreditItems(nextPackagesCreditItems));
    };

    const getDefaultPackageCreditItems = (hasFamilyCredits: boolean, packageItems: PackagesItemByPerson[] = selectedPackageItems): PackagesItemByPerson[] => {
        const defaultCreditPackageCategory = hasFamilyCredits ? PackageCreditItemType.Family : PackageCreditItemType.Standard;
        const defaultCreditPackageAmount = hasFamilyCredits ? DEFAULT_FAMILY_CREDIT_PACKAGE_AMOUNT : DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT;
        const defaultCreditItem = availablePackagesCreditsItems.find(item => {
            if (hasFamilyCredits) {
                return getRecommendedCreditPackageItem(item);
            }
            return (
                item.article.creditPackage?.category === defaultCreditPackageCategory && item.article.creditPackage?.amount_total === defaultCreditPackageAmount
            );
        });

        if (!defaultCreditItem) {
            return [];
        }

        if (hasFamilyCredits) {
            return [
                {
                    personIndex: 0,
                    id: defaultCreditItem.id,
                    person_type: PackageItemPersonType.Adult,
                    quantity: 1,
                },
            ];
        }

        return packageItems.map(item => ({
            personIndex: item.personIndex,
            id: defaultCreditItem.id,
            person_type: item.person_type,
            quantity: 1,
        }));
    };

    const getRecommendedCreditPackageItem = (item: CreditItem) => {
        return peopleCount < 3
            ? item.article.creditPackage?.amount_total === DEFAULT_STANDARD_CREDIT_PACKAGE_AMOUNT
            : peopleCount > 3 && peopleCount < 5
              ? item.article.creditPackage?.amount_total === DEFAULT_FAMILY_CREDIT_PACKAGE_AMOUNT
              : item.article.creditPackage?.amount_total === 128;
    };

    const setDefaultPackageCreditItems = (hasFamilyCredits: boolean, packageItems: PackagesItemByPerson[] = selectedPackageItems): boolean => {
        const defaultPackageCreditItems = getDefaultPackageCreditItems(hasFamilyCredits, packageItems);

        if (defaultPackageCreditItems.length === 0) {
            return false;
        }

        dispatch(BookingAction.setSelectedPackageCreditItems(defaultPackageCreditItems));
        return true;
    };

    /**
     * Retrieves a package item based on the provided duration, karting option, and person type
     *
     * @param {number} duration - The duration of the package item
     * @param {boolean} isKarting - Indicates if karting is included in the package item
     * @param {PackageItemPersonType} personType - The type of person for the package item
     * @returns {undefined | PackagesItemsPerson} - The package item matching the criteria, or undefined if not found
     */
    const getPackageItemForCombination = (
        duration: number | undefined,
        isKarting: boolean,
        personType: PackageItemPersonType
    ): undefined | PackagesItemsPerson => {
        if (!availablePackageItems) {
            return;
        }
        const packageItemIndex = availablePackageItems.packageItems[personType]
            ? availablePackageItems.packageItems[personType].findIndex((item: PackagesItemsPerson) => item.duration === duration && item.karting === isKarting)
            : -1;
        if (packageItemIndex == -1) {
            return;
        }
        return availablePackageItems.packageItems[personType][packageItemIndex];
    };

    /**
     * Retrieves a package item based on the provided duration and person type
     *
     * @param {number} duration - The duration of the package item
     * @param {PackageItemPersonType} personType - The type of person for the package item
     * @returns {undefined | PackagesItemsPerson} - The package item matching the criteria, or undefined if not found
     */
    const getPackageItemByDuration = (duration: number | undefined, personType: PackageItemPersonType): undefined | PackagesItemsPerson => {
        if (!availablePackageItems) {
            return;
        }
        const packageItemIndex = availablePackageItems.packageItems[personType]
            ? availablePackageItems.packageItems[personType].findIndex((item: PackagesItemsPerson) => item.duration === duration && !item.karting)
            : -1;
        if (packageItemIndex == -1) {
            return;
        }
        return availablePackageItems.packageItems[personType][packageItemIndex];
    };

    const getPackageItemPrice = (duration: number | undefined, personType: PackageItemPersonType): number => {
        const packageItem = getPackageItemByDuration(duration, personType);
        if (packageItem) {
            return packageItem.price;
        }
        return 0;
    };

    const manageResetStates = (useCommonDuration: boolean = false) => {
        const adultsCountSaved = selectedPackageItems.filter(item => item.person_type === PackageItemPersonType.Adult).length;
        const childrenCountSaved = selectedPackageItems.filter(item => item.person_type === PackageItemPersonType.Child).length;
        if (filter.adults + filter.children == 0) {
            resetDefaultPackagesItems().then(r => {
                setDefaultPackagesItems(useCommonDuration);
            });
        }

        if (adultsCountSaved !== filter.adults || childrenCountSaved !== filter.children) {
            resetDefaultPackagesItems().then(r => {
                setDefaultPackagesItems(useCommonDuration);
            });
        }
    };

    const getPackageItemsPrice = () => {
        return selectedPackageItems.reduce((total, cartItem) => {
            const packagesItemByPerson = getSavedPackagesItemByPerson(cartItem.personIndex, cartItem.person_type);
            if (filter.category === PackageCategory.FerienPass) {
                if (cartItem.person_type === PackageItemPersonType.Adult && cartItem.personIndex === 0) {
                    return total;
                }
            }
            if (!packagesItemByPerson) {
                return total;
            }
            return total + (packagesItemByPerson?.price || 0);
        }, 0);
    };

    const getCreditItemsPrice = () => {
        return selectedPackageCreditItems?.reduce((total, cartItem) => {
            const creditItem = getPackageCreditItemById(cartItem.id);
            if (!creditItem) {
                return total;
            }
            return total + (creditItem.price || 0) * (cartItem.quantity || 1);
        }, 0);
    };

    const getCompetitionsPrice = () => {
        return selectedCompetitions?.reduce((total, item) => {
            if (item.is_free) {
                return total;
            }
            return total + (item.packageItem.price || 0) * (item.count || 1);
        }, 0);
    };

    const getExtrasPrice = () => {
        return selectedUpsells?.reduce((total, item) => {
            return total + (item.packageItem.price || 0) * (item.count || 1);
        }, 0);
    };

    const getTotalPrice = () => {
        const packageItemsPrice = getPackageItemsPrice();
        const creditItemsPrice = getCreditItemsPrice();
        const competitionsPrice = getCompetitionsPrice();
        const extrasPrice = getExtrasPrice();
        return packageItemsPrice + creditItemsPrice + competitionsPrice + extrasPrice;
    };

    const getDiscountPrice = () => {
        return selectedPackageItems.reduce((total, cartItem) => {
            if (cartItem.person_type !== PackageItemPersonType.Child) {
                return total;
            }
            const childItem = getSavedPackagesItemByPerson(cartItem.personIndex, cartItem.person_type);
            if (!childItem) {
                return total;
            }
            const adultItem = getPackageItemForCombination(childItem.duration, childItem.karting, PackageItemPersonType.Adult);
            if (!adultItem) {
                return total;
            }
            return total + Math.max(0, adultItem.price - childItem.price);
        }, 0);
    };

    const resetDefaultPackagesItems = async (resetCreditItems: boolean = true) => {
        await dispatch(BookingAction.resetSelectedPackageItems());
        if (resetCreditItems) {
            await dispatch(BookingAction.resetSelectedPackageCreditItems());
        }
    };

    const getFilledCount = () => {
        return selectedPackageItems.filter(cartItem => cartItem.id !== null).length;
    };

    const getBookingPackageItems = () => {
        const validIds = selectedPackageItems.filter(cartItem => cartItem.id);
        return validIds.map(cartItem => cartItem.id) as number[];
    };
    const getBookingPackageItemsForEvent = () => {
        const validIds = selectedPackageItems.filter(cartItem => cartItem.id);
        const result = validIds
            .map(cartItem => {
                if (cartItem.id) {
                    return {[cartItem.id]: cartItem.quantity};
                }
            })
            .filter(item => item != null);

        return result;
    };

    const getPackageCreditItemById = (id: number | null): CreditItem | null => {
        if (!id) return null;
        return availablePackagesCreditsItems.find(item => item.id === id) || null;
    };

    const getBookingPackageItemsWithQuantity = () => {
        const validIds = selectedPackageItems.filter(cartItem => cartItem.id);
        return validIds.map(cartItem => cartItem.id) as number[];
    };

    return {
        getLeastPossibleDuration,
        getSavedPackagesItemByPerson,
        setPackageItemHandler,
        setPackageCreditItemHandler,
        setDefaultPackagesItems,
        setDefaultPackageCreditItems,
        manageResetStates,
        getPackageItemForCombination,
        getTotalPrice,
        getFilledCount,
        getBookingPackageItems,
        getBookingPackageItemsForEvent,
        resetDefaultPackagesItems,
        getPackageItemByDuration,
        getPackageCreditItemById,
        setPackageCreditItemsHandler,
        getPackageItemPrice,
        getPackageItemsPrice,
        getRecommendedCreditPackageItem,
        getCreditItemsPrice,
        getCompetitionsPrice,
        getExtrasPrice,
        getDiscountPrice,
        filter,
    };
};

export const usePackagesCartPricing = (reservation: Reservation) => {
    const packagesCartItems =
        reservation?.packagesCart && "packagesCartItems" in reservation.packagesCart
            ? reservation.packagesCart.packagesCartItems.sort((a, b) => (a.packageItem!.article_id > b.packageItem!.article_id ? 1 : -1))
            : [];

    const packagesCartItemVouchers =
        reservation?.packagesCart && "packagesCartItemVouchers" in reservation.packagesCart ? reservation.packagesCart?.packagesCartItemVouchers : [];

    const filterItemsByType = (itemType: PackageItemItemType): PackagesCartItem[] =>
        packagesCartItems.filter(packagesCartItem => packagesCartItem.packageItem?.type == itemType);

    const priceByType = (itemType: PackageItemItemType): number =>
        filterItemsByType(itemType).reduce((prev, cur: PackagesCartItem) => prev + (cur.price || 0), 0);

    return {
        packagesCartItems,
        packagesCartItemVouchers,
        filterItemsByType,
        priceByType,
    };
};

export const getAvailableKartingDuration = (timeslot: Timeslot) => {
    if (!timeslot.karting_available_array || Object.keys(timeslot.karting_available_array).length === 0) {
        return 0;
    }

    const available_array = Object.keys(timeslot.karting_available_array).filter(key => key != "0");
    const values = available_array.map(key => timeslot.karting_available_array[key]);
    const allTrue = values.every(value => value);
    const allFalse = values.every(value => !value);

    if (allFalse) return -100;
    if (allTrue) return 100;
    return 0;
};

export const getDurationOptions = (isDisabled2Hours: boolean, isDisable1Hour: boolean) => {
    const {t} = useTranslation();
    return isDisabled2Hours
        ? [
              {name: 3 + " " + t("hours"), enabled: true, value: 180},
              {name: 4 + " " + t("hours"), enabled: true, value: 240},
              {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
              {name: t("Day ticket"), enabled: true, value: 480},
          ]
        : isDisable1Hour
          ? [
                {name: 2 + " " + t("hours"), enabled: true, value: 120},
                {name: 3 + " " + t("hours"), enabled: true, value: 180},
                {name: 4 + " " + t("hours"), enabled: true, value: 240},
                {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
                {name: t("Day ticket"), enabled: true, value: 480},
            ]
          : [
                {name: 1 + " " + t("hour"), enabled: true, value: 60},
                {name: 2 + " " + t("hours"), enabled: true, value: 120},
                {name: 3 + " " + t("hours"), enabled: true, value: 180},
                {name: 4 + " " + t("hours"), enabled: true, value: 240},
                {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
                {name: t("Day ticket"), enabled: true, value: 480},
            ];
};
